import React, { FC, ChangeEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import GET_WDT_STUDENT_SETTINGS from 'graph/settings/wdt/get-wdt-student-settings-query';
import UPDATE_WDT_STUDENT_CHARACTER_ORDER_SETTING from 'graph/settings/wdt/update-wdt-student-character-order-setting-mutation';
import REMOVE_STUDENT_SETTING from 'graph/settings/common/remove-student-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import {
  Student,
  WdtSettingCharacterOrderData,
  WdtCharacterOrder
} from 'types/schema-generated-types';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface LanguageOptionsInputs {
  label: string;
  value: string;
}

interface CharacterOrderProps {
  student: Student;
  setting: WdtSettingCharacterOrderData | null;
  classroomSetting: WdtSettingCharacterOrderData | null;
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    }
  };
});

const CLASSROOM_LEVEL_SETTING_PLAINTEXT_MAP = {
  [WdtCharacterOrder.hwt]: 'HWT Order',
  [WdtCharacterOrder.natural]: 'Alphabetical/Numerical Order',
  [WdtCharacterOrder.freePlay]: 'Free Play'
};

const getClassroomLevelSettingPlaintext = (value: WdtCharacterOrder) => {
  return value
    ? CLASSROOM_LEVEL_SETTING_PLAINTEXT_MAP[value]
    : CLASSROOM_LEVEL_SETTING_PLAINTEXT_MAP[WdtCharacterOrder.hwt];
};

const letterOptions: LanguageOptionsInputs[] = [
  { label: `Use Class Level Setting `, value: '' },
  {
    label: 'HWT Order (Letters in HWT order and numbers in numerical order)',
    value: WdtCharacterOrder.hwt
  },
  {
    label:
      'Alphabetical/Numerical Order (Letters in alphabetical order and numbers in numerical order)',
    value: WdtCharacterOrder.natural
  },
  { label: 'Free Play (All letters and numbers unlocked)', value: WdtCharacterOrder.freePlay }
];

const getStudentWdtSettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_WDT_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (
        cachedData,
        { updateWdtStudentSettingCharacterOrder }
      ): { wdtStudentSettings: any } => {
        const [{ value: characterOrder }] = updateWdtStudentSettingCharacterOrder;
        return {
          wdtStudentSettings: {
            ...cachedData?.wdtStudentSettings,
            characterOrder
          }
        };
      }
    }
  ])
});

const removeStudentSettingMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_WDT_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (cachedData): { wdtStudentSettings: any } => {
        return {
          wdtStudentSettings: {
            ...cachedData?.wdtStudentSettings,
            characterOrder: null
          }
        };
      }
    }
  ])
});

const CharacterOrder: FC<CharacterOrderProps> = ({
  student,
  setting,
  classroomSetting,
  expanded,
  handleChangeExpanded
}) => {
  const { t } = useTranslation('CharacterOrderSetting');
  const classes = useStyles();

  const studentId = +student.id;
  const [, { openAlert }] = useStore();
  const [value, setValue] = useState(setting?.lowercase || '');
  const [classroomSettingValue] = useState(classroomSetting?.lowercase || '');

  const [updateWdtStudentSettingCharacterOrder] = useMutation(
    UPDATE_WDT_STUDENT_CHARACTER_ORDER_SETTING,
    getStudentWdtSettingsMutationCacheUpdate(studentId)
  );
  const [removeStudentSetting] = useMutation(
    REMOVE_STUDENT_SETTING,
    removeStudentSettingMutationCacheUpdate(studentId)
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue((event.target as HTMLInputElement).value);
  };

  const handleOnCancel = (): void => setValue(setting?.lowercase || '');

  const handleOnConfirm = async (): Promise<void> => {
    try {
      if (value === '') {
        await removeStudentSetting({
          variables: {
            where: {
              studentId,
              setting: 'characterOrder',
              appName: 'wdt'
            }
          }
        });
      } else {
        await updateWdtStudentSettingCharacterOrder({
          variables: {
            data: {
              studentId,
              value: {
                lowercase: value,
                numbers: value,
                capitals: value
              }
            }
          }
        });
      }
      openAlert(getSettingSuccessAlert('Letter and Number Order', t));
    } catch (error) {
      //TODO
      console.log('Error');
    }
  };

  const updatedLetterOptions = letterOptions.map(option => {
    let label = option.label;
    if (!option.value) {
      const classroomSelectedLabel = getClassroomLevelSettingPlaintext(
        classroomSettingValue as WdtCharacterOrder
      );
      label = `${option.label} - ${classroomSelectedLabel}`;
    }

    return { ...option, label };
  });

  return (
    <SettingsCard
      title={t('Letter and Number Order')}
      description={t(
        'Choose the letter and number order students will use (Default is HWT order).'
      )}
      expanded={expanded}
      panel="characterOrder"
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <RadioGroup
          aria-label="letter number setting"
          name="letterNumberSetting"
          value={value}
          onChange={handleChange}
        >
          {updatedLetterOptions.map(
            ({ label, value }, key): ReactElement => (
              <FormControlLabel
                key={`letter-number-option-${key}`}
                value={value}
                control={<Radio />}
                label={label}
              />
            )
          )}
        </RadioGroup>
      </FormControl>
    </SettingsCard>
  );
};

export default CharacterOrder;
